import React, { useEffect, useState } from 'react';
import { slotPolicyApi } from '../api/SlotPolicyApi';
import { standardAreaApi } from '@/features/policy/standardAreaManagement/api/StandardAreaApi';
import {
    SaveSlotPolicyRequest,
    SlotDayItemForm,
    SlotPolicyDto,
    SlotPolicyForm,
} from '../types/slotPolicy.types';
import {
    DAY_LABELS,
    DEFAULT_FORM,
    DEFAULT_SLOT_ITEM,
    DEFAULT_SLOTS,
    VALID_HOURS,
} from '../constants/slotPolicy.constants';
import {
    applySlotInfoTargets,
    fromDateInput,
    getHour,
    pad2,
    slotsHaveOverlap,
    toDateInput,
} from '../utils/slotPolicyUtils';
import '../styles/SlotPolicyModal.css';

interface SlotPolicyModalProps {
    isOpen: boolean;
    editTarget: SlotPolicyDto | null;
    hasGlobalPolicy?: boolean;
    onSave: (req: SaveSlotPolicyRequest) => Promise<void>;
    onClose: () => void;
}

const SlotPolicyModal: React.FC<SlotPolicyModalProps> = ({ isOpen, editTarget, hasGlobalPolicy, onSave, onClose }) => {
    const [form, setForm] = useState<SlotPolicyForm>({ ...DEFAULT_FORM, slots: [{ ...DEFAULT_SLOT_ITEM }] });
    const [saving, setSaving] = useState(false);
    const [availableAreas, setAvailableAreas] = useState<string[]>([]);
    const [areaSearch, setAreaSearch] = useState('');

    useEffect(() => {
        if (!isOpen) return;

        standardAreaApi.getStandardAreas()
            .then(res => setAvailableAreas((res.areas ?? []).map(a => a.areaName)))
            .catch(() => setAvailableAreas([]));
        setAreaSearch('');

        if (editTarget) {
            setForm({
                seqNo: editTarget.seqNo,
                policyName: editTarget.policyName ?? '',
                applyStartDate: editTarget.applyStartDate ?? '',
                requiredSlotCnt: editTarget.requiredSlotCnt != null ? String(editTarget.requiredSlotCnt) : '',
                acceptanceRate: editTarget.acceptanceRate != null ? String(editTarget.acceptanceRate) : '',
                selectedStandardAreas: editTarget.standardAreas ?? [],
                useYn: editTarget.useYn ?? 'Y',
                isDefault: !(editTarget.standardAreas?.length),
                slots: editTarget.slots?.length
                    ? editTarget.slots.map(s => ({
                        seqNo: s.seqNo,
                        slotName: s.slotName ?? '',
                        days: (s.days ?? []).map(d => ({
                            dayOfWeek: d.dayOfWeek,
                            startTime: d.startTime ?? '',
                            endTime: d.endTime ?? '',
                            targetCnt: d.targetCnt ?? '',
                        })),
                    }))
                    : [{ ...DEFAULT_SLOT_ITEM }],
            });
        } else {
            slotPolicyApi.getSlotInfoDefaults()
                .then(res => {
                    setForm({ ...DEFAULT_FORM, slots: applySlotInfoTargets(res.codes ?? []) });
                })
                .catch(() => {
                    setForm({ ...DEFAULT_FORM, slots: DEFAULT_SLOTS });
                });
        }
    }, [isOpen, editTarget]);

    if (!isOpen) return null;

    const setField = (key: keyof SlotPolicyForm, value: string) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const setSlotName = (idx: number, value: string) =>
        setForm(prev => {
            const slots = [...prev.slots];
            slots[idx] = { ...slots[idx], slotName: value };
            return { ...prev, slots };
        });

    const setDayField = (slotIdx: number, dayIdx: number, key: keyof SlotDayItemForm, value: string) =>
        setForm(prev => {
            const slots = [...prev.slots];
            const days = [...slots[slotIdx].days];
            days[dayIdx] = { ...days[dayIdx], [key]: value };
            slots[slotIdx] = { ...slots[slotIdx], days };
            return { ...prev, slots };
        });

    const handleDefaultToggle = async () => {
        if (!form.isDefault) {
            if (hasGlobalPolicy && !editTarget) {
                alert('전체 협력사 적용(기본) 정책은 하나만 등록할 수 있습니다.');
                return;
            }
            try {
                const res = await slotPolicyApi.getSlotInfoDefaults();
                setForm(prev => ({
                    ...prev,
                    isDefault: true,
                    selectedStandardAreas: [],
                    slots: applySlotInfoTargets(res.codes ?? []),
                }));
            } catch {
                setForm(prev => ({
                    ...prev,
                    isDefault: true,
                    selectedStandardAreas: [],
                    slots: DEFAULT_SLOTS,
                }));
            }
        } else {
            setForm(prev => ({ ...prev, isDefault: false }));
        }
    };

    const addSlot = () =>
        setForm(prev => ({ ...prev, slots: [...prev.slots, { ...DEFAULT_SLOT_ITEM }] }));

    const removeSlot = (index: number) =>
        setForm(prev => ({ ...prev, slots: prev.slots.filter((_, i) => i !== index) }));

    const handleSave = async () => {
        if (!form.policyName.trim()) {
            alert('그룹명은 필수입니다.');
            return;
        }
        if (form.slots.some(s => !s.slotName.trim())) {
            alert('슬롯명은 필수입니다.');
            return;
        }
        if (slotsHaveOverlap(form.slots)) {
            alert('슬롯 시간대가 겹칩니다. 시간을 다시 확인해주세요.');
            return;
        }

        const req: SaveSlotPolicyRequest = {
            seqNo: form.seqNo,
            policyName: form.policyName.trim(),
            applyStartDate: form.applyStartDate.trim(),
            requiredSlotCnt: form.requiredSlotCnt !== '' ? Number(form.requiredSlotCnt) : null,
            acceptanceRate: form.acceptanceRate !== '' ? Number(form.acceptanceRate) : null,
            standardAreas: form.selectedStandardAreas,
            slots: form.slots.map((s, i) => ({
                seqNo: s.seqNo,
                slotOrder: i + 1,
                slotName: s.slotName.trim(),
                days: s.days.map(d => ({
                    dayOfWeek: d.dayOfWeek,
                    startTime: d.startTime,
                    endTime: d.endTime,
                    targetCnt: d.targetCnt,
                })),
            })),
            userId: '',
        };

        setSaving(true);
        try {
            await onSave(req);
        } finally {
            setSaving(false);
        }
    };

    const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="slot-modal-overlay" onClick={handleBackdrop}>
            <div className="slot-modal" onClick={e => e.stopPropagation()}>
                {/* 헤더 */}
                <div className="slot-modal-header">
                    <h2 className="slot-modal-title">
                        {editTarget ? '슬롯 정책 수정' : '슬롯 정책 등록'}
                    </h2>
                </div>

                {/* 본문 */}
                <div className="slot-modal-body">
                    {/* 기본 정보 */}
                    <div className="slot-form-grid">
                        <div className="slot-form-group">
                            <label className="slot-form-label required">그룹명</label>
                            <input
                                className="slot-form-input"
                                type="text"
                                placeholder="예) 안성A"
                                value={form.policyName}
                                onChange={e => setField('policyName', e.target.value)}
                            />
                        </div>

                        <div className="slot-form-group">
                            <label className="slot-form-label">적용 시작일</label>
                            <input
                                className="slot-form-input"
                                type="date"
                                value={toDateInput(form.applyStartDate)}
                                onChange={e => setField('applyStartDate', fromDateInput(e.target.value))}
                            />
                            {!form.applyStartDate && (
                                <span className="slot-form-hint">비워두면 오늘부터 즉시 적용됩니다</span>
                            )}
                        </div>

                        {/* 필수 달성 슬롯 수 - 당장 불필요하여 숨김
                        <div className="slot-form-group">
                            <label className="slot-form-label">필수 달성 슬롯 수</label>
                            <input
                                className="slot-form-input"
                                type="number"
                                min={1}
                                max={4}
                                placeholder="예) 3"
                                value={form.requiredSlotCnt}
                                onChange={e => setField('requiredSlotCnt', e.target.value)}
                            />
                        </div>

                        <div className="slot-form-group">
                            <label className="slot-form-label">수락률 (%)</label>
                            <input
                                className="slot-form-input"
                                type="number"
                                min={0}
                                max={100}
                                placeholder="예) 90"
                                value={form.acceptanceRate}
                                onChange={e => setField('acceptanceRate', e.target.value)}
                            />
                        </div>
                        */}

                        <div className="slot-form-group">
                            <label className="slot-form-label">사용 여부</label>
                            <select
                                className="slot-form-select"
                                value={form.useYn}
                                onChange={e => setField('useYn', e.target.value)}
                            >
                                <option value="Y">Y (사용)</option>
                                <option value="N">N (미사용)</option>
                            </select>
                        </div>

                        <div className="slot-form-group full-width">
                            <label className="slot-form-label">
                                표준권역
                                <span className="slot-form-label-hint">
                                    {form.isDefault ? '기본' : `(${form.selectedStandardAreas.length}개 선택됨)`}
                                </span>
                            </label>
                            <label className="slot-area-item slot-area-default-toggle">
                                <input
                                    type="checkbox"
                                    checked={form.isDefault}
                                    onChange={handleDefaultToggle}
                                />
                                기본 (모든 협력사에 적용되는 기본 정책)
                            </label>
                            <input
                                className="slot-form-input slot-area-search"
                                type="text"
                                placeholder="권역명 검색"
                                value={areaSearch}
                                onChange={e => setAreaSearch(e.target.value)}
                                disabled={form.isDefault}
                            />
                            <div className="slot-area-list">
                                {availableAreas.length === 0 ? (
                                    <div className="slot-area-empty">
                                        {areaSearch
                                            ? '검색 결과가 없습니다.'
                                            : '등록된 표준권역이 없습니다. 표준권역 관리에서 먼저 등록해주세요.'}
                                    </div>
                                ) : (
                                    availableAreas
                                        .filter(a => a.toLowerCase().includes(areaSearch.toLowerCase()))
                                        .map(areaName => (
                                            <label
                                                key={areaName}
                                                className={`slot-area-item${form.isDefault ? ' slot-area-item--disabled' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={form.selectedStandardAreas.includes(areaName)}
                                                    disabled={form.isDefault}
                                                    onChange={() =>
                                                        setForm(prev => ({
                                                            ...prev,
                                                            selectedStandardAreas: prev.selectedStandardAreas.includes(areaName)
                                                                ? prev.selectedStandardAreas.filter(a => a !== areaName)
                                                                : [...prev.selectedStandardAreas, areaName],
                                                        }))
                                                    }
                                                />
                                                {areaName}
                                            </label>
                                        ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 슬롯 목록 */}
                    <div className="slot-section-title">
                        <span>슬롯 설정</span>
                        <button className="btn-add-slot" type="button" onClick={addSlot}>
                            + 슬롯 추가
                        </button>
                    </div>

                    <div className="slot-table-scroll">
                        <table className="slot-rows-table">
                            <thead>
                            <tr>
                                <th style={{ minWidth: 110 }} rowSpan={2}>슬롯명</th>
                                {DAY_LABELS.map(d => (
                                    <th key={d} colSpan={3} style={{ minWidth: 180, textAlign: 'center' }}>{d}</th>
                                ))}
                                <th style={{ minWidth: 44 }} rowSpan={2}></th>
                            </tr>
                            <tr>
                                {DAY_LABELS.map(d => (
                                    <React.Fragment key={d}>
                                        <th style={{ minWidth: 56, fontWeight: 400, fontSize: 11 }}>시작</th>
                                        <th style={{ minWidth: 56, fontWeight: 400, fontSize: 11 }}>종료</th>
                                        <th style={{ minWidth: 50, fontWeight: 400, fontSize: 11 }}>건수</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {form.slots.map((slot, slotIdx) => (
                                <tr key={slotIdx}>
                                    <td>
                                        <input
                                            className="slot-cell-input name-input"
                                            type="text"
                                            placeholder="슬롯명"
                                            value={slot.slotName}
                                            onChange={e => setSlotName(slotIdx, e.target.value)}
                                        />
                                    </td>
                                    {slot.days.map((day, dayIdx) => (
                                        <React.Fragment key={day.dayOfWeek}>
                                            <td>
                                                <select
                                                    className="slot-cell-input"
                                                    value={getHour(day.startTime)}
                                                    onChange={e =>
                                                        setDayField(slotIdx, dayIdx, 'startTime', `${pad2(Number(e.target.value))}:00`)
                                                    }
                                                >
                                                    <option value="">--</option>
                                                    {VALID_HOURS.map(h => (
                                                        <option key={h} value={h}>{pad2(h)}:00</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <select
                                                    className="slot-cell-input"
                                                    value={getHour(day.endTime)}
                                                    onChange={e =>
                                                        setDayField(slotIdx, dayIdx, 'endTime', `${pad2(Number(e.target.value))}:59`)
                                                    }
                                                >
                                                    <option value="">--</option>
                                                    {VALID_HOURS.map(h => (
                                                        <option key={h} value={h}>{pad2(h)}:59</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    className="slot-cell-input"
                                                    type="number"
                                                    min={0}
                                                    placeholder="0"
                                                    value={day.targetCnt}
                                                    onChange={e => setDayField(slotIdx, dayIdx, 'targetCnt', e.target.value)}
                                                />
                                            </td>
                                        </React.Fragment>
                                    ))}
                                    <td className="slot-remove-cell">
                                        <button
                                            className="btn-remove-slot"
                                            type="button"
                                            onClick={() => removeSlot(slotIdx)}
                                            disabled={form.slots.length === 1}
                                        >
                                            ✕
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 푸터 */}
                <div className="slot-modal-footer">
                    <button className="btn-modal-cancel" type="button" onClick={onClose}>
                        취소
                    </button>
                    <button
                        className="btn-modal-save"
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? '저장 중...' : '저장'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SlotPolicyModal;
